// src/pages/Users/TechniquesQuizz/TechniquesQuizz.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import useScrollToTop from "../../../hooks/useScollToTop";
import {
  getTechniquesJudo,
  getJudoTechniquesWithoutSelfDefense,
} from "../../../services/techniquesServices";

import QuizHeader from "./QuizHeader";
import QuizControls from "./QuizControls";
import ProgressBar from "./ProgressBar";
import QuizQuestion from "./QuizQuestion";
import QuizResults from "./QuizResults";
import { buildQuestions } from "./quizUtils";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";

const GRADES = [
  "Toutes les ceintures",
  "Jaune",
  "Orange",
  "Verte",
  "Bleue",
  "Marron",
  "Noire",
];

export default function TechniquesQuizz() {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  // Données
  const [techniques, setTechniques] = useState([]);
  const [loading, setLoading] = useState(true);

  // Options quiz
  const [grade, setGrade] = useState("Toutes les ceintures");
  const [includeSelfDefense, setIncludeSelfDefense] = useState(false);

  // État du quiz
  const [questions, setQuestions] = useState([]); // [{image, correctAnswer, options, famille, id}]
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null); // option choisie pour feedback
  const [score, setScore] = useState(0);
  const [errors, setErrors] = useState([]); // [{q, selected}]
  const [finished, setFinished] = useState(false);

  // Fetch techniques
  const fetchTechniques = useCallback(async () => {
    try {
      setLoading(true);
      const data = includeSelfDefense
        ? await getTechniquesJudo()
        : await getJudoTechniquesWithoutSelfDefense();
      setTechniques(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }, [includeSelfDefense]);

  useEffect(() => {
    fetchTechniques();
  }, [fetchTechniques]);

  // (Re)génération des questions quand techniques/grade changent
  useEffect(() => {
    if (!techniques?.length) return;
    const qs = buildQuestions(techniques, { grade, count: 10 });
    setQuestions(qs);
    setCurrent(0);
    setSelected(null);
    setScore(0);
    setErrors([]);
    setFinished(false);
  }, [techniques, grade]);

  const progress = useMemo(() => {
    const total = Math.max(questions.length, 1);
    // si on a sélectionné une réponse sur la question en cours, on compte quand même la progression visuelle
    const answeredCount = Math.min(current + (selected ? 1 : 0), total);
    return (answeredCount / total) * 100;
  }, [current, selected, questions.length]);

  const onAnswer = (option) => {
    if (selected) return; // ne pas re-cliquer
    setSelected(option);

    const q = questions[current];
    if (option === q.correctAnswer) {
      setScore((s) => s + 1);
    } else {
      setErrors((prev) => [...prev, { q, selected: option }]);
    }
  };

  const nextQuestion = () => {
    if (!selected) return; // empêche avance sans choix
    if (current >= questions.length - 1) {
      setFinished(true);
    } else {
      setCurrent((c) => c + 1);
      setSelected(null);
    }
  };

  const restart = () => {
    const qs = buildQuestions(techniques, { grade, count: 10 });
    setQuestions(qs);
    setCurrent(0);
    setSelected(null);
    setScore(0);
    setErrors([]);
    setFinished(false);
  };

  return (
    <section className="w-full">
          {/* Offset navbar */}
          <div className="pt-[150px]" aria-hidden />
    
          <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
            {/* Header page */}
            <HeaderTitle
              role="Utilisateur"
              title="Quizz Techniques"
              align="center"
              className="mb-1"
            />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Navigation */}
        <div className="mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Carte principale */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur">
          {/* Header gradient */}
          <QuizHeader
            title="Techniques Quizz"
            subtitle="Devine la technique à partir de l’image — 10 questions, 4 propositions."
          />

          {/* Contenu */}
          <div className="p-4 sm:p-6">
            {/* Filtres */}
            {!finished && (
              <div className="mb-5">
                <QuizControls
                  grades={GRADES}
                  grade={grade}
                  onGradeChange={setGrade}
                  includeSelfDefense={includeSelfDefense}
                  onToggleSelfDefense={() =>
                    setIncludeSelfDefense((v) => !v)
                  }
                  disabled={loading}
                />
              </div>
            )}

            {/* Loading */}
            {loading && (
              <div className="space-y-3">
                <div className="h-6 w-40 rounded bg-slate-200 dark:bg-slate-800 animate-pulse" />
                <div className="h-2 w-full rounded bg-slate-200 dark:bg-slate-800 animate-pulse" />
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
                  <div className="h-64 rounded-xl bg-slate-200 dark:bg-slate-800 animate-pulse lg:col-span-2" />
                  <div className="space-y-3">
                    {[...Array(4)].map((_, i) => (
                      <div
                        key={i}
                        className="h-12 rounded-xl bg-slate-200 dark:bg-slate-800 animate-pulse"
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Quiz en cours */}
            {!loading && !finished && questions.length > 0 && (
              <>
                {/* Progression */}
                <div className="mb-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm sm:text-base font-semibold text-slate-900 dark:text-slate-100">
                      Question {current + 1} / {questions.length}
                    </p>
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      Score : <span className="font-semibold">{score}</span>
                    </p>
                  </div>
                  <ProgressBar value={progress} />
                </div>

                <QuizQuestion
                  question={questions[current]}
                  selected={selected}
                  onAnswer={onAnswer}
                  onNext={nextQuestion}
                  hasNext={current < questions.length - 1}
                />
              </>
            )}

            {/* Résultats */}
            {!loading && finished && (
              <QuizResults
                questions={questions}
                errors={errors}
                score={score}
                onRestart={restart}
              />
            )}

            {/* Si pas assez de données */}
            {!loading && !finished && questions.length === 0 && (
              <div className="text-center py-16 text-slate-600 dark:text-slate-300">
                Pas assez de techniques pour générer des questions avec ces
                critères. Essaie un autre grade ou active la Self-Défense.
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
    </section>
  );
}
